import type { AcademicTerm } from '../types/domain';
import {
  ensureAnnualProgressMetaQueryData,
  ensureAnnualTimetableCellsQueryData,
  readAnnualProgressMetaCacheOrQuery,
  readAnnualTimetableCellsCacheOrQuery,
  readProgramFixedRowsCacheOrQuery,
  readProgramProgressCellsCacheOrQuery,
  readProgramScopedRowsCacheOrQuery,
} from '../query/readSchoolQueryCache';
import { queryClient } from '../query/queryClient';
import { programFixedRowsQueryOptions } from '../query/hooks/useProgramFixedRowsQuery';
import { programProgressCellsQueryOptions } from '../query/hooks/useProgramProgressCellsQuery';
import { programScopedRowsQueryOptions } from '../query/hooks/useProgramScopedRowsQuery';
import { loadAnnualProgressDataCache, saveAnnualProgressDataCache } from './annualProgressDataCache';
import { dashboardLessonTermContentNeedsWarmFromFlags } from './dashboardCalendarLessonTermWarmFlags';
import { resolveTermNumberForViewDate } from './dashboardLessonSectionResolve';

export { dashboardLessonTermContentNeedsWarmFromFlags } from './dashboardCalendarLessonTermWarmFlags';

export function dashboardLessonTermContentNeedsWarm(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): boolean {
  const tt = readAnnualTimetableCellsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const sc = readProgramScopedRowsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const pp = readProgramProgressCellsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const fx = readProgramFixedRowsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  return dashboardLessonTermContentNeedsWarmFromFlags({
    timetableFound: tt.found,
    scopedFound: sc.found,
    progressFound: pp.found,
    progressCoverage: pp.coverage,
    fixedFound: fx.found,
  });
}

function mirrorAnnualProgressMetaToTermCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  const meta = readAnnualProgressMetaCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  if (!meta.found) return;
  const existing = loadAnnualProgressDataCache(workspaceId, academicYear, termNumber);
  saveAnnualProgressDataCache(workspaceId, academicYear, termNumber, {
    terms: meta.data.terms,
    schedule: meta.data.schedule,
    tracks: meta.data.tracks,
    regularColors: meta.data.regularColors,
    dedicatedColors: meta.data.dedicatedColors,
    baseExcludeColors: meta.data.baseExcludeColors,
    holidayDays: existing.found ? existing.state.holidayDays : [],
    offDayByDate: existing.found ? existing.state.offDayByDate ?? {} : {},
    offDaysHydrated: existing.found ? !!existing.state.offDaysHydrated : false,
    cellsByKey: {},
    holidayHydrated: existing.found ? !!existing.state.holidayHydrated : false,
    cellsHydrated: true,
  });
}

/** 데일리·위클리·캘린더에 보이는 날짜가 속한 학기(1·2) */
export function collectDashboardVisibleLessonTermNumbers(
  terms: AcademicTerm[],
  dateIsos: readonly string[]
): Array<1 | 2> {
  const termSet = new Set<1 | 2>();
  for (const iso of dateIsos) {
    const trimmed = iso.trim();
    if (!trimmed) continue;
    termSet.add(resolveTermNumberForViewDate(terms, trimmed));
  }
  return Array.from(termSet).sort((a, b) => a - b) as Array<1 | 2>;
}

/**
 * 대시보드(데일리·위클리·캘린더)가 해당 학기를 보여줘야 하는데 연간진도표 캐시가 없으면
 * 연간진도표 페이지와 같은 RQ 키로 fetch해 LS·RQ를 만든다.
 */
export async function warmDashboardLessonTermContent(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): Promise<boolean> {
  if (!workspaceId.trim() || !gradeId.trim()) return false;
  const tt = readAnnualTimetableCellsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const sc = readProgramScopedRowsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const pp = readProgramProgressCellsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const fx = readProgramFixedRowsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const jobs: Promise<unknown>[] = [
    ensureAnnualProgressMetaQueryData({ workspaceId, academicYear, termNumber, gradeId }),
  ];
  if (!tt.found) {
    jobs.push(ensureAnnualTimetableCellsQueryData({ workspaceId, academicYear, termNumber, gradeId }));
  }
  if (!pp.found || pp.coverage === 'partial') {
    jobs.push(
      queryClient.ensureQueryData(
        programProgressCellsQueryOptions({
          workspaceId,
          academicYear,
          termNumber,
          gradeId,
          forceRefresh: pp.coverage === 'partial' && pp.found,
        })
      )
    );
  }
  if (!sc.found) {
    jobs.push(
      queryClient.ensureQueryData(
        programScopedRowsQueryOptions({ workspaceId, academicYear, termNumber, gradeId })
      )
    );
  }
  if (!fx.found) {
    jobs.push(
      queryClient.ensureQueryData(
        programFixedRowsQueryOptions({ workspaceId, academicYear, termNumber, gradeId })
      )
    );
  }
  const fetchedContent = jobs.length > 1;
  await Promise.all(jobs);
  mirrorAnnualProgressMetaToTermCache(workspaceId, academicYear, termNumber, gradeId);
  return fetchedContent;
}
